import {
  Upload,
  Card,
  Col,
  Form,
  Input,
  Button,
  Divider,
  Spin,
  Image,
  Row,
  Popconfirm
} from 'antd';
import { useFileUploadHook } from 'hooks/FileUpload.hook';
import {
  EditOutlined,
  QuestionCircleOutlined,
  UploadOutlined
} from '@ant-design/icons';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColProps } from 'antd/lib/col';
import './Brands.scss';
import { rootContext } from 'stores';
import { IBrand } from 'types';
import { observer } from 'mobx-react';
import { capitalize, toast, toastTypeEnum } from 'common/utils';

const Brands: React.FC = observer(() => {
  const [editData, setEditData] = useState<IBrand | null>(null);
  const [borderStyle, setBorderStyle] = useState({});
  const {
    BrandStore: {
      addBand,
      editBrand,
      deleteBrand,
      state: { data, ui },
      getBrands
    }
  } = useContext(rootContext);
  useEffect(() => {
    getBrands();
  }, [getBrands]);

  const colProp: ColProps = {
    offset: 2,
    xxl: 4,
    xl: 4,
    xs: 16
  };
  const allowedType = ['image/jpeg', 'image/png', 'image/svg+xml'];
  const brandLogoFileUploadHook = useFileUploadHook(allowedType);
  const navigate = useNavigate();
  const [brandForm] = Form.useForm();
  const resetForm = () => {
    brandForm.resetFields();
    brandLogoFileUploadHook.onRemove();
    setEditData(null);
  };
  const validations = (rule: any, value: any, callback: any) => {
    if (value && value?.trim()?.length === 0) {
      return Promise.reject('Whitespaces are not valid.');
    } else {
      return Promise.resolve();
    }
  };
  return (
    <div className="BrandsStyles">
      <div className="cardWrapeer">
        <Card>
          <Form
            className="brandform__block"
            style={borderStyle}
            form={brandForm}
            layout="inline"
            onFinish={async values => {
              if (brandLogoFileUploadHook.fileList.length < 1 && !editData) {
                return toast(toastTypeEnum.error, 'Logo is Required');
              }
              const params = {
                brand: values.brand as string,
                brandLogo: brandLogoFileUploadHook.fileList[0] as File
              };
              let success;
              if (editData) {
                success = await editBrand({ id: editData.id, ...params });
              } else {
                success = await addBand(params);
              }
              if (success) {
                resetForm();
                getBrands();
              }
            }}
          >
            <Row gutter={[16, 0]} justify="space-between">
              <Col {...colProp} xs={24} md={8} offset={0}>
                <Form.Item
                  label="Name"
                  name="brand"
                  rules={[
                    {
                      required: true,
                      message: 'Please Provide Brand Name'
                    },
                    { validator: validations }
                  ]}
                >
                  <Input placeholder="Brand Name" />
                </Form.Item>
              </Col>
              <Col {...colProp} xs={24} md={8} offset={0}>
                <Form.Item
                  label="Logo"
                  name="brandLogo"
                  rules={
                    editData
                      ? []
                      : [
                          {
                            required: true,
                            message: 'Please Attach Image'
                          }
                        ]
                  }
                  initialValue={editData?.url}
                >
                  <Upload
                    {...brandLogoFileUploadHook}
                    accept="image/x-png,image/jpeg,image/svg+xml"
                  >
                    <Button icon={<UploadOutlined />}>Click to upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              <Col {...colProp} xs={24} md={4} offset={0}>
                {editData ? (
                  brandLogoFileUploadHook.fileList.length > 0 ? (
                    <Image
                      className="preview"
                      src={URL.createObjectURL(
                        brandLogoFileUploadHook.fileList[0]
                      )}
                      alt="  "
                    />
                  ) : (
                    <Image className="preview" src={editData.url} />
                  )
                ) : (
                  brandLogoFileUploadHook.fileList.length > 0 && (
                    <Image
                      className="preview"
                      src={URL.createObjectURL(
                        brandLogoFileUploadHook.fileList[0]
                      )}
                      alt="  "
                    />
                  )
                )}
              </Col>
              <Col
                {...colProp}
                xs={24}
                md={4}
                offset={0}
                className="add__brands__btn__col"
              >
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="primary_btn"
                    style={{
                      width: '180px'
                    }}
                    loading={ui.addBrandsLoading}
                  >
                    {editData ? 'Update Brand' : 'Add Brand'}
                  </Button>
                  {editData && (
                    <div>
                      <Button
                        htmlType="reset"
                        type="default"
                        onClick={resetForm}
                        disabled={Boolean(
                          ui.deleteBrandsLoading || ui.addBrandsLoading
                        )}
                      >
                        Discard
                      </Button>

                      <Popconfirm
                        onConfirm={async () => {
                          let success =
                            editData?.id && (await deleteBrand(editData.id));
                          if (success) {
                            resetForm();
                            getBrands();
                          }
                        }}
                        onCancel={e => e?.stopPropagation()}
                        title="Are you sure？"
                        icon={
                          <QuestionCircleOutlined style={{ color: 'red' }} />
                        }
                      >
                        <Button
                          danger
                          onClick={e => {
                            e.stopPropagation();
                          }}
                          loading={ui.deleteBrandsLoading}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Divider>All Brands</Divider>
          <div className="squareListContainer">
            {ui.getBrandsLoading && <Spin className="flexCenter" />}
            {data.brands.map((item: IBrand) => (
              <div className="brandContainer" key={item.id}>
                <p>{capitalize(item.brand)}</p>
                <div className="imgContainer">
                  <img src={item.url} alt={item.brand} />
                </div>
                <Button
                  onClick={() => {
                    window.scroll({ top: 0, behavior: 'smooth' });
                    setBorderStyle({
                      border: '2px solid white',
                      boxShadow: '0px 0px 20px'
                    });
                    setTimeout(() => {
                      setBorderStyle({
                        border: '1px solid gray',
                        boxShadow: '0px 0px 10px'
                      });
                    }, 1000);
                    const values = { ...item, brand: capitalize(item.brand) };
                    brandLogoFileUploadHook.onRemove();
                    brandForm.resetFields();
                    brandForm.setFieldsValue(values);
                    setEditData(values);
                  }}
                >
                  <EditOutlined />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
});

export default Brands;
